import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { PlusIcon, TrashIcon, SearchIcon, MapPinIcon, BookOpenIcon, XIcon, InfoIcon, FileTextIcon, FileIcon, FilePlusIcon } from 'lucide-react';
import { getDefaultCurrency } from '../../utils/appSettings';
import { FileUpload } from '../FileUpload';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
export function RequisitionForm({
  onSave,
  locale = 'en-US'
}) {
  // Generate a system requisition number
  const generateRequisitionNumber = () => {
    const currentYear = new Date().getFullYear();
    const randomDigits = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
    return `REQ-${currentYear}-${randomDigits}`;
  };
  const [formData, setFormData] = useState({
    name: generateRequisitionNumber(),
    vendor: '',
    vendorId: '',
    purchaseJustification: '',
    vendorJustification: '',
    businessCase: '',
    urgencyLevel: 'normal',
    items: [{
      description: '',
      quantity: 1,
      unitPrice: 0,
      type: 'Item',
      uom: 'EA',
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      catalogItemId: '',
      glCode: '',
      itemCode: '' // Added to match PO form
    }],
    notes: '',
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: '',
    deliveryDate: '',
    deliveryAddress: {
      type: 'entity',
      addressId: '',
      customAddress: {
        name: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      }
    },
    currency: getDefaultCurrency().code,
    documents: [] // Add documents array to store uploaded files with document types
  });
  // Document types for the dropdown
  const documentTypes = ['Quote', 'Invoice', 'Delivery Docket', 'Specification Sheet', 'Contract', 'Certificate', 'Compliance Document', 'Technical Drawing', 'Warranty Information', 'Other'];
  // Add errors state
  const [errors, setErrors] = useState({
    lineItems: []
  });
  // Sample vendors for the dropdown
  const [vendors, setVendors] = useState([{
    id: 'V001',
    name: 'Office Depot',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V002',
    name: 'Dell Technologies',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V003',
    name: 'PrintWorks Inc.',
    country: 'Canada',
    hasCatalog: false
  }, {
    id: 'V004',
    name: 'Server Solutions Inc.',
    country: 'United Kingdom',
    hasCatalog: true
  }, {
    id: 'V005',
    name: 'Global Supplies Co.',
    country: 'Australia',
    hasCatalog: false
  }]);
  // Sample catalog items for vendors
  const [vendorCatalogs, setVendorCatalogs] = useState({
    V001: [{
      id: 'CAT001-001',
      name: 'Premium Paper - A4',
      description: 'Premium quality A4 paper, 500 sheets',
      type: 'Item',
      uom: 'EA',
      unitPrice: 12.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-002',
      name: 'Ballpoint Pens - Blue',
      description: 'Pack of 10 blue ballpoint pens',
      type: 'Item',
      uom: 'EA',
      unitPrice: 5.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-003',
      name: 'Office Chair - Ergonomic',
      description: 'Ergonomic office chair with lumbar support',
      type: 'Item',
      uom: 'EA',
      unitPrice: 249.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-004',
      name: 'Desk Organizer',
      description: 'Desktop organizer with multiple compartments',
      type: 'Item',
      uom: 'EA',
      unitPrice: 19.99,
      taxCode: 'STD',
      taxRate: 10
    }],
    V002: [{
      id: 'CAT002-001',
      name: 'Laptop - Business Series',
      description: 'Business laptop with 16GB RAM, 512GB SSD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1299.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-002',
      name: 'Desktop Computer - Standard',
      description: 'Standard desktop computer for office use',
      type: 'Item',
      uom: 'EA',
      unitPrice: 899.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-003',
      name: 'Monitor - 24 inch',
      description: '24-inch LED monitor, Full HD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 199.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-004',
      name: 'IT Support - Basic',
      description: 'Basic IT support package, 10 hours',
      type: 'Service',
      uom: 'HR',
      unitPrice: 75.0,
      taxCode: 'STD',
      taxRate: 10
    }],
    V004: [{
      id: 'CAT004-001',
      name: 'Server - Entry Level',
      description: 'Entry level server for small businesses',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1499.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-002',
      name: 'Server - Enterprise',
      description: 'Enterprise grade server with high performance',
      type: 'Item',
      uom: 'EA',
      unitPrice: 3999.99,
      taxCode: 'STD',
      taxRate: 10
    }]
  });
  // Sample addresses for different contexts
  const [addresses, setAddresses] = useState({
    tenant: [{
      id: 'TA001',
      name: 'Corporate HQ',
      street: '123 Main St',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
      country: 'United States'
    }, {
      id: 'TA002',
      name: 'Distribution Center',
      street: '456 Warehouse Blvd',
      city: 'Chicago',
      state: 'IL',
      postalCode: '60007',
      country: 'United States'
    }],
    entity: [{
      id: 'EA001',
      name: 'North America Office',
      street: '789 Business Park',
      city: 'Boston',
      state: 'MA',
      postalCode: '02108',
      country: 'United States'
    }, {
      id: 'EA002',
      name: 'European Office',
      street: '10 Tech Square',
      city: 'London',
      postalCode: 'EC1A 1BB',
      country: 'United Kingdom'
    }],
    site: [{
      id: 'SA001',
      name: 'Manufacturing Plant',
      street: '567 Industrial Way',
      city: 'Detroit',
      state: 'MI',
      postalCode: '48127',
      country: 'United States'
    }, {
      id: 'SA002',
      name: 'Research Lab',
      street: '890 Innovation Dr',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94107',
      country: 'United States'
    }]
  });
  const [showCustomAddress, setShowCustomAddress] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [filteredVendors, setFilteredVendors] = useState(vendors);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [currentLineItemIndex, setCurrentLineItemIndex] = useState(0);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [filteredCatalogItems, setFilteredCatalogItems] = useState([]);
  // Unit of measure options
  const uomOptions = ['EA', 'KG', 'LB', 'M', 'CM', 'L', 'ML', 'HR', 'DAY', 'MONTH', 'YEAR'];
  // Tax codes with descriptions and rates
  const taxCodes = [{
    code: 'STD',
    name: 'Standard Rate',
    rate: 10
  }, {
    code: 'RED',
    name: 'Reduced Rate',
    rate: 5
  }, {
    code: 'ZERO',
    name: 'Zero Rate',
    rate: 0
  }, {
    code: 'EXM',
    name: 'Exempt',
    rate: 0
  }];
  // Urgency levels
  const urgencyLevels = [{
    value: 'low',
    label: 'Low - No specific deadline'
  }, {
    value: 'normal',
    label: 'Normal - Required in standard timeframe'
  }, {
    value: 'high',
    label: 'High - Required soon'
  }, {
    value: 'critical',
    label: 'Critical - Urgent requirement'
  }];
  useEffect(() => {
    // Filter vendors based on search query
    if (vendorSearchQuery) {
      const filtered = vendors.filter(vendor => vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase()) || vendor.id.toLowerCase().includes(vendorSearchQuery.toLowerCase()));
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(vendors);
    }
  }, [vendorSearchQuery, vendors]);
  useEffect(() => {
    // Filter catalog items based on search query
    if (showCatalogModal && formData.vendorId) {
      const catalog = vendorCatalogs[formData.vendorId] || [];
      if (catalogSearchQuery) {
        const filtered = catalog.filter(item => item.name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.description.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.id.toLowerCase().includes(catalogSearchQuery.toLowerCase()));
        setFilteredCatalogItems(filtered);
      } else {
        setFilteredCatalogItems(catalog);
      }
    }
  }, [catalogSearchQuery, formData.vendorId, showCatalogModal, vendorCatalogs]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    if (field === 'taxCode') {
      // Find the tax rate for the selected tax code
      const selectedTaxCode = taxCodes.find(tax => tax.code === value);
      const taxRate = selectedTaxCode ? selectedTaxCode.rate : 0;
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        taxRate: taxRate
      };
      // Recalculate tax amount
      const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
      updatedItems[index].taxAmount = subtotal * taxRate / 100;
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value
      };
      // Recalculate tax amount if quantity or unit price changes
      if (field === 'quantity' || field === 'unitPrice') {
        const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
        updatedItems[index].taxAmount = subtotal * updatedItems[index].taxRate / 100;
      }
    }
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        description: '',
        quantity: 1,
        unitPrice: 0,
        type: 'Item',
        uom: 'EA',
        taxCode: 'STD',
        taxRate: 10,
        taxAmount: 0,
        catalogItemId: '',
        glCode: '',
        itemCode: ''
      }]
    });
  };
  const removeItem = index => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };
  const calculateTaxTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.taxAmount, 0);
  };
  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxTotal();
  };
  const handleVendorSelect = vendor => {
    setFormData({
      ...formData,
      vendor: vendor.name,
      vendorId: vendor.id
    });
    setVendorSearchQuery('');
  };
  const handleAddressTypeChange = e => {
    const addressType = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        type: addressType,
        addressId: ''
      }
    });
    setShowCustomAddress(addressType === 'custom');
  };
  const handleAddressSelect = e => {
    const addressId = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        addressId: addressId
      }
    });
  };
  const handleCustomAddressChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        customAddress: {
          ...formData.deliveryAddress.customAddress,
          [name.replace('customAddress_', '')]: value
        }
      }
    });
  };
  const openCatalogModal = index => {
    setCurrentLineItemIndex(index);
    setCatalogSearchQuery('');
    // Reset filtered catalog items to show all items for the vendor
    if (formData.vendorId && vendorCatalogs[formData.vendorId]) {
      setFilteredCatalogItems(vendorCatalogs[formData.vendorId]);
    } else {
      setFilteredCatalogItems([]);
    }
    setShowCatalogModal(true);
  };
  const closeCatalogModal = () => {
    setShowCatalogModal(false);
    setCatalogSearchQuery('');
  };
  const handleCatalogItemSelect = catalogItem => {
    const updatedItems = [...formData.items];
    // Calculate tax amount
    const taxAmount = catalogItem.unitPrice * catalogItem.taxRate / 100;
    // Update the line item with catalog item details
    updatedItems[currentLineItemIndex] = {
      description: catalogItem.description,
      quantity: 1,
      unitPrice: catalogItem.unitPrice,
      type: catalogItem.type,
      uom: catalogItem.uom,
      taxCode: catalogItem.taxCode,
      taxRate: catalogItem.taxRate,
      taxAmount: taxAmount,
      catalogItemId: catalogItem.id,
      itemCode: catalogItem.id // Set the itemCode to the catalog item's ID
    };
    setFormData({
      ...formData,
      items: updatedItems
    });
    closeCatalogModal();
  };
  // Check if the selected vendor has a catalog
  const selectedVendorHasCatalog = () => {
    if (!formData.vendorId) return false;
    const vendor = vendors.find(v => v.id === formData.vendorId);
    return vendor ? vendor.hasCatalog : false;
  };
  const handleFileUpload = filesWithTypes => {
    // Convert the file objects to a format that can be stored in state
    const newDocuments = filesWithTypes.map(({
      file,
      documentType
    }) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      documentType: documentType,
      file: file,
      uploadDate: new Date().toISOString()
    }));
    setFormData({
      ...formData,
      documents: [...formData.documents, ...newDocuments]
    });
  };
  const removeDocument = index => {
    const updatedDocuments = [...formData.documents];
    updatedDocuments.splice(index, 1);
    setFormData({
      ...formData,
      documents: updatedDocuments
    });
  };
  const updateDocumentType = (index, newType) => {
    const updatedDocuments = [...formData.documents];
    updatedDocuments[index] = {
      ...updatedDocuments[index],
      documentType: newType
    };
    setFormData({
      ...formData,
      documents: updatedDocuments
    });
  };
  const handleSubmit = (e, isDraft = true) => {
    e.preventDefault();
    // Validate line items if not draft
    if (!isDraft) {
      // Validate line items
      const lineItemErrors = formData.items.map(item => {
        const itemErrors = {};
        if (!item.description) {
          itemErrors.description = 'Description is required';
        }
        if (!item.quantity || item.quantity <= 0) {
          itemErrors.quantity = 'Quantity must be greater than 0';
        }
        return Object.keys(itemErrors).length > 0 ? itemErrors : null;
      });
      // Update errors state
      setErrors({
        lineItems: lineItemErrors
      });
      // Only save if there are no errors
      if (lineItemErrors.some(error => error !== null)) {
        return;
      }
    }
    onSave({
      ...formData,
      total: calculateTotal()
    }, isDraft);
  };
  return <form onSubmit={e => handleSubmit(e, false)} data-id="element-15126">
      <div className="shadow sm:rounded-md sm:overflow-hidden" data-id="element-15127">
        <div className="px-4 py-5 bg-white space-y-6 sm:p-6" data-id="element-15128">
          <div className="grid grid-cols-6 gap-6" data-id="element-15129">
            <div className="col-span-6 sm:col-span-3" data-id="element-15130">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-15131">
                Requisition Number
              </label>
              <input type="text" name="name" id="name" value={formData.name} disabled className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 bg-gray-50 rounded-md cursor-not-allowed" data-id="element-15132" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-15133">
                System generated reference number
              </p>
            </div>
            {/* Vendor Lookup */}
            <div className="col-span-6 sm:col-span-3" data-id="element-15134">
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-15135">
                Vendor
              </label>
              <div className="mt-1 relative" data-id="element-15136">
                <div className="flex" data-id="element-15137">
                  <div className="relative flex-grow" data-id="element-15138">
                    <input type="text" id="vendorSearch" value={vendorSearchQuery} onChange={e => setVendorSearchQuery(e.target.value)} placeholder="Search vendors..." className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-15139" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-15140">
                      <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-15141" />
                    </div>
                  </div>
                </div>
                {/* Selected vendor display */}
                {formData.vendor && <div className="mt-2 flex items-center p-2 bg-gray-50 border border-gray-200 rounded-md" data-id="element-15142">
                    <span className="text-sm font-medium text-gray-700" data-id="element-15143">
                      {formData.vendor}
                    </span>
                    <span className="ml-2 text-xs text-gray-500" data-id="element-15144">
                      ({formData.vendorId})
                    </span>
                    {selectedVendorHasCatalog() && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-15145">
                        <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-15146" />
                        Catalog Available
                      </span>}
                  </div>}
                {/* Vendor search results */}
                {vendorSearchQuery && <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md overflow-hidden" data-id="element-15147">
                    <ul className="max-h-60 overflow-auto" data-id="element-15148">
                      {filteredVendors.length > 0 ? filteredVendors.map(vendor => <li key={vendor.id} className="cursor-pointer p-2 hover:bg-gray-50" onClick={() => handleVendorSelect(vendor)} data-id="element-15149">
                            <div className="flex justify-between" data-id="element-15150">
                              <span className="font-medium" data-id="element-15151">{vendor.name}</span>
                              <span className="text-xs text-gray-500" data-id="element-15152">
                                {vendor.id}
                              </span>
                            </div>
                            <div className="flex items-center" data-id="element-15153">
                              <span className="text-xs text-gray-500" data-id="element-15154">
                                {vendor.country}
                              </span>
                              {vendor.hasCatalog && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-15155">
                                  <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-15156" />
                                  Catalog
                                </span>}
                            </div>
                          </li>) : <li className="p-2 text-center text-sm text-gray-500" data-id="element-15157">
                          No vendors found
                        </li>}
                    </ul>
                  </div>}
              </div>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-15158">
              <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700" data-id="element-15159">
                Required Delivery Date
              </label>
              <input type="date" name="deliveryDate" id="deliveryDate" value={formData.deliveryDate} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-15160" />
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-15161">
              <label htmlFor="urgencyLevel" className="block text-sm font-medium text-gray-700" data-id="element-15162">
                Urgency Level
              </label>
              <select id="urgencyLevel" name="urgencyLevel" value={formData.urgencyLevel} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-15163">
                {urgencyLevels.map(level => <option key={level.value} value={level.value} data-id="element-15164">
                    {level.label}
                  </option>)}
              </select>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-15165">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-15166">
                Currency
              </label>
              <div className="mt-1 relative" data-id="element-15167">
                <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-15168">
                  <option value="AUD" data-id="element-15169">AUD - Australian Dollar</option>
                  <option value="USD" data-id="element-15170">USD - US Dollar</option>
                  <option value="EUR" data-id="element-15171">EUR - Euro</option>
                  <option value="GBP" data-id="element-15172">GBP - British Pound</option>
                  <option value="JPY" data-id="element-15173">JPY - Japanese Yen</option>
                  <option value="CAD" data-id="element-15174">CAD - Canadian Dollar</option>
                  <option value="INR" data-id="element-15175">INR - Indian Rupee</option>
                </select>
                <div className="mt-1 text-xs text-gray-500 flex items-center" data-id="element-15176">
                  <InfoIcon className="h-3 w-3 mr-1 text-blue-500" data-id="element-15177" />
                  Using system default currency
                </div>
              </div>
            </div>
            {/* Purchase Justification Section */}
            <div className="col-span-6" data-id="element-15178">
              <fieldset className="border border-gray-200 rounded-md p-4" data-id="element-15179">
                <legend className="text-sm font-medium text-gray-700 px-2" data-id="element-15180">
                  Purchase Justification
                </legend>
                <div className="mb-4" data-id="element-15181">
                  <label htmlFor="purchaseJustification" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15182">
                    Why is this purchase needed? *
                  </label>
                  <textarea id="purchaseJustification" name="purchaseJustification" rows={3} value={formData.purchaseJustification} onChange={handleChange} placeholder="Explain the business need for this purchase" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15183"></textarea>
                </div>
                <div className="mb-4" data-id="element-15184">
                  <label htmlFor="businessCase" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15185">
                    Business Case
                  </label>
                  <textarea id="businessCase" name="businessCase" rows={3} value={formData.businessCase} onChange={handleChange} placeholder="Provide any ROI calculations or business case details" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15186"></textarea>
                </div>
                <div data-id="element-15187">
                  <label htmlFor="vendorJustification" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15188">
                    Why this vendor? *
                  </label>
                  <textarea id="vendorJustification" name="vendorJustification" rows={3} value={formData.vendorJustification} onChange={handleChange} placeholder="Explain why this vendor is recommended (pricing, quality, service, etc.)" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15189"></textarea>
                </div>
              </fieldset>
            </div>
            {/* Delivery Address Section */}
            <div className="col-span-6" data-id="element-15190">
              <fieldset className="border border-gray-200 rounded-md p-4" data-id="element-15191">
                <legend className="text-sm font-medium text-gray-700 px-2" data-id="element-15192">
                  Delivery Address
                </legend>
                <div className="mb-4" data-id="element-15193">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15194">
                    Address Type
                  </label>
                  <div className="flex space-x-4" data-id="element-15195">
                    <div className="flex items-center" data-id="element-15196">
                      <input id="addressTypeTenant" name="addressType" type="radio" value="tenant" checked={formData.deliveryAddress.type === 'tenant'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-15197" />
                      <label htmlFor="addressTypeTenant" className="ml-2 block text-sm text-gray-700" data-id="element-15198">
                        Tenant Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-15199">
                      <input id="addressTypeEntity" name="addressType" type="radio" value="entity" checked={formData.deliveryAddress.type === 'entity'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-15200" />
                      <label htmlFor="addressTypeEntity" className="ml-2 block text-sm text-gray-700" data-id="element-15201">
                        Entity Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-15202">
                      <input id="addressTypeSite" name="addressType" type="radio" value="site" checked={formData.deliveryAddress.type === 'site'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-15203" />
                      <label htmlFor="addressTypeSite" className="ml-2 block text-sm text-gray-700" data-id="element-15204">
                        Site Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-15205">
                      <input id="addressTypeCustom" name="addressType" type="radio" value="custom" checked={formData.deliveryAddress.type === 'custom'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-15206" />
                      <label htmlFor="addressTypeCustom" className="ml-2 block text-sm text-gray-700" data-id="element-15207">
                        Custom Address
                      </label>
                    </div>
                  </div>
                </div>
                {formData.deliveryAddress.type !== 'custom' && <div className="mb-4" data-id="element-15208">
                    <label htmlFor="addressSelect" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15209">
                      Select Address
                    </label>
                    <select id="addressSelect" value={formData.deliveryAddress.addressId} onChange={handleAddressSelect} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-15210">
                      <option value="" data-id="element-15211">Select an address</option>
                      {addresses[formData.deliveryAddress.type].map(address => <option key={address.id} value={address.id} data-id="element-15212">
                            {address.name} - {address.street}, {address.city}
                          </option>)}
                    </select>
                    {/* Display selected address details */}
                    {formData.deliveryAddress.addressId && <div className="mt-3 p-3 bg-gray-50 rounded-md" data-id="element-15213">
                        {(() => {
                    const selectedAddress = addresses[formData.deliveryAddress.type].find(addr => addr.id === formData.deliveryAddress.addressId);
                    if (selectedAddress) {
                      return <div className="flex items-start" data-id="element-15214">
                                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-2" data-id="element-15215" />
                                <div data-id="element-15216">
                                  <p className="text-sm font-medium" data-id="element-15217">
                                    {selectedAddress.name}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-15218">
                                    {selectedAddress.street},{' '}
                                    {selectedAddress.city},
                                    {selectedAddress.state && ` ${selectedAddress.state},`}{' '}
                                    {selectedAddress.postalCode}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-15219">
                                    {selectedAddress.country}
                                  </p>
                                </div>
                              </div>;
                    }
                    return null;
                  })()}
                      </div>}
                  </div>}
                {formData.deliveryAddress.type === 'custom' && <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-15220">
                    <div className="sm:col-span-6" data-id="element-15221">
                      <label htmlFor="customAddress_name" className="block text-sm font-medium text-gray-700" data-id="element-15222">
                        Address Name/Label
                      </label>
                      <input type="text" name="customAddress_name" id="customAddress_name" value={formData.deliveryAddress.customAddress.name} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., Warehouse, Client Site" data-id="element-15223" />
                    </div>
                    <div className="sm:col-span-6" data-id="element-15224">
                      <label htmlFor="customAddress_street" className="block text-sm font-medium text-gray-700" data-id="element-15225">
                        Street Address
                      </label>
                      <input type="text" name="customAddress_street" id="customAddress_street" value={formData.deliveryAddress.customAddress.street} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-15226" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-15227">
                      <label htmlFor="customAddress_city" className="block text-sm font-medium text-gray-700" data-id="element-15228">
                        City
                      </label>
                      <input type="text" name="customAddress_city" id="customAddress_city" value={formData.deliveryAddress.customAddress.city} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-15229" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-15230">
                      <label htmlFor="customAddress_state" className="block text-sm font-medium text-gray-700" data-id="element-15231">
                        State / Province
                      </label>
                      <input type="text" name="customAddress_state" id="customAddress_state" value={formData.deliveryAddress.customAddress.state} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-15232" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-15233">
                      <label htmlFor="customAddress_postalCode" className="block text-sm font-medium text-gray-700" data-id="element-15234">
                        Postal Code
                      </label>
                      <input type="text" name="customAddress_postalCode" id="customAddress_postalCode" value={formData.deliveryAddress.customAddress.postalCode} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-15235" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-15236">
                      <label htmlFor="customAddress_country" className="block text-sm font-medium text-gray-700" data-id="element-15237">
                        Country
                      </label>
                      <input type="text" name="customAddress_country" id="customAddress_country" value={formData.deliveryAddress.customAddress.country} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-15238" />
                    </div>
                  </div>}
              </fieldset>
            </div>
          </div>
          <div data-id="element-15239">
            <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-15240">
              Line Items
            </h3>
            <div className="mt-4 space-y-4" data-id="element-15241">
              {formData.items.map((item, index) => <div key={index} className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-15242">
                  <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-15243">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-15244">
                      Item {index + 1}
                    </h4>
                    <button type="button" onClick={() => removeItem(index)} className="text-red-600 hover:text-red-900" data-id="element-15245">
                      <TrashIcon className="h-5 w-5" data-id="element-15246" />
                    </button>
                  </div>
                  {item.catalogItemId && <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md" data-id="element-15247">
                      <div className="flex items-center" data-id="element-15248">
                        <BookOpenIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-15249" />
                        <span className="text-sm font-medium text-blue-700" data-id="element-15250">
                          Catalog Item: {item.catalogItemId}
                        </span>
                      </div>
                    </div>}
                  <div className="p-4" data-id="element-15251">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6" data-id="element-15252">
                      <div className="sm:col-span-3" data-id="element-15253">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15254">
                          Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.type} onChange={e => handleItemChange(index, 'type', e.target.value)} data-id="element-15255">
                          <option value="Item" data-id="element-15256">Item</option>
                          <option value="Service" data-id="element-15257">Service</option>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-15258">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15259">
                          Item Code
                        </label>
                        <div className="flex" data-id="element-15260">
                          <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.itemCode || ''} onChange={e => handleItemChange(index, 'itemCode', e.target.value)} placeholder="Item code" data-id="element-15261" />
                          <button type="button" onClick={() => openCatalogModal(index)} className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15262">
                            Select from Catalog
                          </button>
                        </div>
                      </div>
                      <div className="sm:col-span-6" data-id="element-15263">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15264">
                          Description <span className="text-red-500" data-id="element-15265">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.description ? 'border-red-300' : ''}`} value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} placeholder="Brief description" data-id="element-15266" />
                        {errors.lineItems && errors.lineItems[index]?.description && <p className="mt-1 text-sm text-red-600" data-id="element-15267">
                              {errors.lineItems[index].description}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-15268">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15269">
                          Quantity <span className="text-red-500" data-id="element-15270">*</span>
                        </label>
                        <input type="number" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.quantity ? 'border-red-300' : ''}`} value={item.quantity} onChange={e => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} min="0" step="1" data-id="element-15271" />
                        {errors.lineItems && errors.lineItems[index]?.quantity && <p className="mt-1 text-sm text-red-600" data-id="element-15272">
                              {errors.lineItems[index].quantity}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-15273">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15274">
                          Unit of Measure
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.uom} onChange={e => handleItemChange(index, 'uom', e.target.value)} data-id="element-15275">
                          {uomOptions.map(uom => <option key={uom} value={uom} data-id="element-15276">
                              {uom}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-15277">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15278">
                          Unit Price <span className="text-red-500" data-id="element-15279">*</span>
                        </label>
                        <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" data-id="element-15280" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-15281">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15282">
                          Tax Code
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.taxCode} onChange={e => handleItemChange(index, 'taxCode', e.target.value)} data-id="element-15283">
                          {taxCodes.map(code => <option key={code.code} value={code.code} data-id="element-15284">
                              {code.code} - {code.name}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-15285">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15286">
                          Tax Amount
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-sm font-medium" data-id="element-15287">
                          {formatCurrency(item.taxAmount || 0, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                      <div className="sm:col-span-2" data-id="element-15288">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15289">
                          GL Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.glCode || ''} onChange={e => handleItemChange(index, 'glCode', e.target.value)} placeholder="GL account code" data-id="element-15290" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-15291">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15292">
                          Line Total
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-lg font-medium" data-id="element-15293">
                          {formatCurrency(item.quantity * item.unitPrice, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
              <div className="mt-2" data-id="element-15294">
                <button type="button" onClick={addItem} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15295">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-15296" />
                  Add Line Item
                </button>
              </div>
            </div>
          </div>
          {/* Upload Documents Section */}
          <div data-id="element-15297">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4" data-id="element-15298">
              Supporting Documents
            </h3>
            <FileUpload onFileUpload={handleFileUpload} documentTypes={documentTypes} data-id="element-15299" />
            {formData.documents.length > 0 && <div className="mt-4" data-id="element-15300">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-15301">
                  Uploaded Documents
                </h4>
                <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-15302">
                  <ul className="divide-y divide-gray-200" data-id="element-15303">
                    {formData.documents.map((doc, index) => <li key={index} className="px-4 py-3 flex items-center justify-between" data-id="element-15304">
                        <div className="flex items-center" data-id="element-15305">
                          <FileIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-15306" />
                          <div data-id="element-15307">
                            <p className="text-sm font-medium text-gray-900" data-id="element-15308">
                              {doc.name}
                            </p>
                            <div className="flex items-center mt-1" data-id="element-15309">
                              <div className="mr-2" data-id="element-15310">
                                <select value={doc.documentType} onChange={e => updateDocumentType(index, e.target.value)} className="text-xs border-gray-300 rounded-md py-1 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" data-id="element-15311">
                                  {documentTypes.map(type => <option key={type} value={type} data-id="element-15312">
                                      {type}
                                    </option>)}
                                </select>
                              </div>
                              <span className="text-xs text-gray-500" data-id="element-15313">
                                {(doc.size / 1024).toFixed(1)} KB
                              </span>
                              <span className="text-xs text-gray-500 ml-2" data-id="element-15314">
                                Uploaded:{' '}
                                {new Date(doc.uploadDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button type="button" onClick={() => removeDocument(index)} className="text-red-600 hover:text-red-900" data-id="element-15315">
                          <TrashIcon className="h-5 w-5" data-id="element-15316" />
                        </button>
                      </li>)}
                  </ul>
                </div>
              </div>}
          </div>
          <div data-id="element-15317">
            <StandardizedNotes value={formData.notes} onChange={value => setFormData({
            ...formData,
            notes: value
          })} visibility={formData.noteVisibility} onVisibilityChange={visibility => setFormData({
            ...formData,
            noteVisibility: visibility
          })} visibilityDetails={formData.noteVisibilityDetails} onVisibilityDetailsChange={details => setFormData({
            ...formData,
            noteVisibilityDetails: details
          })} label="Additional Notes" placeholder="Add any additional notes or special instructions. Select who can view these notes using the visibility options." data-id="element-15318" />
          </div>
          <div className="border-t border-gray-200 pt-4" data-id="element-15319">
            <div className="flex justify-end" data-id="element-15320">
              <div className="w-64 bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-15321">
                <div className="flex justify-between items-center text-sm" data-id="element-15322">
                  <span className="text-gray-700" data-id="element-15323">Subtotal:</span>
                  <span className="font-medium" data-id="element-15324">
                    {formatCurrency(calculateSubtotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2" data-id="element-15325">
                  <span className="text-gray-700" data-id="element-15326">Tax:</span>
                  <span className="font-medium" data-id="element-15327">
                    {formatCurrency(calculateTaxTotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between items-center" data-id="element-15328">
                  <span className="text-gray-900 font-medium" data-id="element-15329">Total:</span>
                  <span className="text-lg font-bold" data-id="element-15330">
                    {formatCurrency(calculateTotal(), formData.currency, locale)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 space-x-3" data-id="element-15331">
          <button type="button" onClick={e => handleSubmit(e, true)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15332">
            Save as Draft
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15333">
            Submit for Approval
          </button>
        </div>
      </div>
      {/* Catalog Item Selection Modal */}
      {showCatalogModal && <div className="fixed z-50 inset-0 overflow-y-auto" data-id="element-15334">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-15335">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={closeCatalogModal} data-id="element-15336">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-15337"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-15338">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-15339">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-15340">
                <div className="sm:flex sm:items-start" data-id="element-15341">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-15342">
                    <div className="flex justify-between items-center mb-4" data-id="element-15343">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15344">
                        Select Item from Catalog
                      </h3>
                      <button type="button" onClick={closeCatalogModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-15345">
                        <XIcon className="h-6 w-6" data-id="element-15346" />
                      </button>
                    </div>
                    <div className="mb-4" data-id="element-15347">
                      <div className="relative rounded-md shadow-sm" data-id="element-15348">
                        <input type="text" value={catalogSearchQuery} onChange={e => setCatalogSearchQuery(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search catalog items..." data-id="element-15349" />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-15350">
                          <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-15351" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 max-h-96 overflow-y-auto" data-id="element-15352">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-15353">
                        <thead className="bg-gray-50" data-id="element-15354">
                          <tr data-id="element-15355">
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15356">
                              Item Code
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15357">
                              Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15358">
                              Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15359">
                              UoM
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15360">
                              Price
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15361">
                              Tax
                            </th>
                            <th scope="col" className="relative px-6 py-3" data-id="element-15362">
                              <span className="sr-only" data-id="element-15363">Select</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-15364">
                          {filteredCatalogItems.length > 0 ? filteredCatalogItems.map(catalogItem => <tr key={catalogItem.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleCatalogItemSelect(catalogItem)} data-id="element-15365">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15366">
                                  {catalogItem.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15367">
                                  <div data-id="element-15368">
                                    <div className="font-medium text-gray-900" data-id="element-15369">
                                      {catalogItem.name}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-15370">
                                      {catalogItem.description}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15371">
                                  {catalogItem.type}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15372">
                                  {catalogItem.uom}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15373">
                                  {formatCurrency(catalogItem.unitPrice, formData.currency, locale)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15374">
                                  {catalogItem.taxCode} ({catalogItem.taxRate}%)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-15375">
                                  <button type="button" onClick={e => {
                            e.stopPropagation();
                            handleCatalogItemSelect(catalogItem);
                          }} className="text-indigo-600 hover:text-indigo-900" data-id="element-15376">
                                    Select
                                  </button>
                                </td>
                              </tr>) : <tr data-id="element-15377">
                              <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-15378">
                                {formData.vendorId && vendorCatalogs[formData.vendorId] ? 'No matching catalog items found' : 'No catalog items available for this vendor'}
                              </td>
                            </tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-15379">
                <button type="button" onClick={closeCatalogModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-15380">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </form>;
}